import { useState } from 'react';

import { useRecoilValue } from 'recoil';

import { useUploadFileToR2 } from '@hooks/use-upload-file-to-r2';
import { filterLabelIdsAtom } from '@store/atoms/filter-labelIds';
import { workspaceAtom } from '@store/atoms/workspace';

import { useCreateBrandAsset } from './use-create-brand-asset';
import { ASSET_TYPE_WITH_LABELS } from '../constants';
import { AssetType, RawFileCategory } from '../enums';
import { CreateBrandAssetRequest, Item } from '../types';

export const useBrandLibrary = () => {
  const filterLabelIds = useRecoilValue(filterLabelIdsAtom);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const currentWorkspace = useRecoilValue(workspaceAtom);

  const uploadFileToR2 = useUploadFileToR2();

  const { mutateAsync: createBrandAsset, isLoading: loadingCreateBrandAsset } =
    useCreateBrandAsset();

  const createAssets = async ({
    uploadedData,
    fileName,
    assetType,
    attributeIds,
  }: {
    uploadedData: {
      s3Bucket: string;
      s3Key: string;
    };
    fileName: string;
    assetType: AssetType;
    attributeIds?: number[];
  }) => {
    const brandAsset: CreateBrandAssetRequest = {
      clientId: currentWorkspace.id,
      assetType: assetType,
      fileName,
      s3Key: uploadedData.s3Key,
      s3Bucket: uploadedData.s3Bucket,
    };
    if (assetType === AssetType.RAW_FILE) {
      brandAsset['metadata'] = {
        type: RawFileCategory.BRAND_BOOK,
      };
      if (attributeIds) {
        brandAsset['attributeTypeIds'] = attributeIds;
      }
    }
    if (ASSET_TYPE_WITH_LABELS.includes(assetType)) {
      brandAsset['labelIds'] = filterLabelIds;
    }

    await createBrandAsset(brandAsset);
  };

  const handleUploadFiles = async ({
    uploadedFiles,
    assetType,
    attributeIds,
  }: {
    uploadedFiles: File[];
    assetType: AssetType;
    attributeIds?: number[];
  }) => {
    const fileName = uploadedFiles[0]?.name;
    const response = await uploadFileToR2({
      file: uploadedFiles[0],
      filename: fileName,
    });
    createAssets({
      uploadedData: response,
      fileName,
      assetType,
      attributeIds,
    });
  };

  const handleSave = async ({
    items,
    handleClose,
    assetType,
    attributeIds,
  }: {
    items: Item[];
    handleClose: () => void;
    assetType: AssetType;
    attributeIds?: number[];
  }) => {
    const uploadFiles: File[] = [];
    setIsUploading(true);

    await Promise.all(
      items.map(async item => {
        const response = await fetch(item.thumbnailUrl);
        const blob = await response.blob();
        const file = new File([blob], `${item.name}`, { type: blob.type });
        uploadFiles.push(file);
      }),
    );
    uploadFiles.forEach(file => {
      handleUploadFiles({
        uploadedFiles: [file],
        assetType: assetType as AssetType,
        attributeIds,
      });
    });

    setIsUploading(false);

    handleClose();
  };

  return {
    handleUploadFiles,
    handleSave,
    isUploading,
    createBrandAsset,
    loadingCreateBrandAsset,
  };
};
